import React, { useEffect, useState } from "react";
import { getSpas, createSpa, updateSpa, deleteSpa } from "./api";
import { capitalizeName, getUserInfo, highlightFirstMatch } from "utils/common";
import {
  DEBOUNCE_DELAY,
  initialMetaForTable,
  LOGIN_ROLES,
} from "constants/common";
import { Pencil, Plus } from "phosphor-react";
import { useDispatch } from "react-redux";
import TableWrapper from "components/common/table-wrapper";
import Tooltip from "components/common/tooltip";
import AddEditSpa from "components/spa/AddEditSpa";
import CustomModal from "components/common/modal";

let timeout;

const Spa = () => {
  const [spas, setSpas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(initialMetaForTable);

  const [showModal, setShowModal] = useState(false);
  const [editSpa, setEditSpa] = useState(null);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Only Outreach Leader or Super Admin can see this tab
  const user = getUserInfo();
  const isAllowed =
    user?.type === LOGIN_ROLES.outreachleader || user?.super_admin;

  const handleAdd = () => {
    setEditSpa(null);
    setShowModal(true);
    setError("");
  };

  const handleEdit = (spa) => {
    setEditSpa(spa);
    setShowModal(true);
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    setLoading(true);
    try {
      await deleteSpa(id);
      fetchSpas();
    } catch (e) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError("");
    // Enforce unique zip code
    // const duplicate = spas.find(
    //   (spa) => spa.zip_code_id === values.zip_code_id && spa.id !== values.id
    // );
    // if (duplicate) {
    //   setError("Duplicate Zip Code is not allowed.");
    //   setLoading(false);
    //   return;
    // }
    try {
      if (values.id) {
        await updateSpa(values.id, values);
      } else {
        await createSpa({ spa_number: values });
      }
      setShowModal(false);
      fetchSpas();
    } catch (e) {
      setError("Error saving entry.");
    } finally {
      setLoading(false);
    }
  };

  const debounceFn = (callback, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(callback, delay);
  };

  const handleSetSearchQuery = (value) => {
    let newMetaObj = { ...meta, search: value };
    setMeta(newMetaObj);
    debounceFn(() => {
      fetchSpas(newMetaObj);
    }, DEBOUNCE_DELAY);
  };

  const handlePageChange = (value) => {
    let newMetaObj = { ...meta, page: value };
    setMeta(newMetaObj);
    debounceFn(() => {
      fetchSpas(newMetaObj);
    }, DEBOUNCE_DELAY);
  };
  const fetchSpas = async (metaObj = meta) => {
    setLoading(true);
    try {
      const result = await getSpas({
        ...metaObj,
        key: sortConfig.key,
        order: sortConfig.direction,
      });
      if (result?.spa_numbers) {
        setSpas(result?.spa_numbers || []);
        setMeta((prev) => ({
          ...prev,
          perPage: result?.meta?.per_page,
          totalCount: result?.meta?.total_count,
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSpas();
  }, [sortConfig.direction, sortConfig.key]);
  const COLUMNS = [
    { columnKey: "zip_code", label: "Zip Code", isSortable: true },
    { columnKey: "release_type", label: "SPA Number", isSortable: true },
    { columnKey: "created_at", label: "Last Updated", isSortable: true },
  ];

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const SortableTableHeader = ({
    columnKey,
    label,
    sortConfig,
    onSortChange,
  }) => {
    const isSorting = sortConfig.key === columnKey;
    const isAscending = isSorting && sortConfig.direction === "asc";
    const isDescending = isSorting && sortConfig.direction === "desc";

    const handleSort = () => {
      if (isAscending) {
        onSortChange(columnKey, "desc");
      } else {
        onSortChange(columnKey, "asc");
      }
    };

    return (
      <th onClick={handleSort} style={{ cursor: "pointer" }}>
        <div className="d-flex position-relative">
          <div className="d-flex flex-column cursor-pointer me-2 position-absolute top-50  end-100 translate-middle-y ">
            {/* <CaretUp size={12} color={isAscending ? '#000000' : '#00000050'} weight="fill" />
            <CaretDown color={isDescending ? '#000000' : '#00000050'} size={12} weight="fill" /> */}
          </div>
          <div>{label}</div>
        </div>
      </th>
    );
  };

  return (
    <>
      <div className="main-contents">
        <h1 className="heading-h5">SPA</h1>
        <TableWrapper
          searchPlaceholder="Search SPA Number"
          setSearhQuery={handleSetSearchQuery}
          searchValue={meta?.search}
          totalListCount={meta?.totalCount || 0}
          pageSize={meta?.perPage}
          currentPage={meta?.page}
          onPageChange={handlePageChange}
          isLoading={loading}
          actionButtons={[
            {
              label: "Add SPA",
              classes: "primary-btn",
              icon: <Plus size={24} className="me-3" />,
              handleClick: () => handleAdd(),
            },
          ]}
        >
          <div className="container-fluid">
            <div className="table-responsive">
              <table className="table table-rows">
                <thead className="table-header">
                  <tr>
                    {COLUMNS.map((column) => (
                      <SortableTableHeader
                        key={column.columnKey}
                        columnKey={column.columnKey}
                        label={column.label}
                        isSortable={column.isSortable}
                        sortConfig={sortConfig}
                        onSortChange={handleSort}
                      />
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {spas?.map((spa, index) => (
                    <tr key={index}>
                      <td>{spa?.zip_code?.code}</td>
                      <td>{spa?.spa_code}</td>
                      <td>{spa?.updated_at}</td>

                      <td>
                        <Tooltip title="Edit">
                          <Pencil
                            size={24}
                            className="opacity-50"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleEdit(spa)}
                          />
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TableWrapper>
        {showModal && (
          <CustomModal
            size="sm"
            show
            onHide={() => setShowModal(false)}
            heading={`${editSpa ? "Edit" : "Add"} SPA`}
          >
            <AddEditSpa
              handleSubmit={handleSubmit}
              handleClose={() => setShowModal(false)}
              spaToEdit={editSpa || {}}
            />
          </CustomModal>
        )}
      </div>
    </>
  );
};

export default Spa;
